import * as React from 'react';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Engagement activity pulse + live refresh.
 *
 * `GET /api/v1/engagements/:id/activity` returns an opaque change `cursor`
 * plus what is still in flight (documents being parsed / indexed, AI runs,
 * proposals awaiting review). See apps/api services/engagement-activity.ts.
 *
 * `useEngagementLiveRefresh` is mounted ONCE per engagement (in the
 * engagement layout route). It polls the pulse and, whenever the cursor
 * moves, invalidates every cached query scoped to that engagement — so
 * Baseline, Deliverables, Checkpoints, the review queue, the overview,
 * Health Check, everything — refetches without the user navigating away
 * and back. This is what makes an upload visibly ripple through the app.
 *
 * Why invalidate by predicate instead of listing keys: almost every
 * engagement-scoped hook in lib/api puts the engagement id in its query
 * key. Matching on that id covers ~60 key namespaces and any added later;
 * a hand-maintained list is exactly how the tabs went stale in the first
 * place (the upload modal invalidated documents and nothing else). The few
 * namespaces keyed by an entity id instead are listed explicitly below.
 *
 * Cadence: 4s while anything is processing, 12s when idle. React Query
 * pauses interval polling while the browser tab is hidden, so an idle
 * background tab costs nothing.
 */

export const EngagementActivitySchema = z.object({
  cursor: z.string(),
  latest_change_at: z.string().nullable(),
  pending_proposals: z.number(),
  processing: z.object({ documents: z.number(), ai_runs: z.number() }),
});
export type EngagementActivity = z.infer<typeof EngagementActivitySchema>;

const ACTIVE_POLL_MS = 4_000;
const IDLE_POLL_MS = 12_000;

/** Namespace for the pulse query itself — excluded from the invalidation sweep. */
const ACTIVITY_KEY = 'engagement-activity';

/**
 * Query namespaces keyed by an ENTITY id (a SOW, a checkpoint, a
 * deliverable…) rather than the engagement id, so the id-match predicate
 * can't see them. They only ever render inside an engagement, and
 * invalidation refetches mounted queries only, so sweeping them on any
 * engagement change is safe and cheap.
 */
const ENTITY_SCOPED_NAMESPACES = new Set([
  'change-order',
  'checkpoint-deliverables',
  'checkpoint-assessment',
  'checkpoint-assessments',
  'deliverable-history',
  'deviation',
  'document-status',
  'document-tree',
  'pm-sync-logs',
  'sow',
]);

export function useEngagementActivityQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: [ACTIVITY_KEY, engagementId] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/activity`,
        EngagementActivitySchema,
      ),
    enabled: Boolean(engagementId),
    staleTime: 0,
    // Interval polling pauses while the browser tab is hidden. Coming back
    // to the tab polls at once, so a consultant returning from another
    // window sees current data immediately instead of up to 12s later.
    // (The app-wide default is refetchOnWindowFocus: false.)
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const p = q.state.data?.processing;
      return p && p.documents + p.ai_runs > 0 ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    },
    // A failed poll must not surface as a page error or hammer the API.
    retry: false,
  });
}

/**
 * Poll the pulse and refetch the engagement's cached queries when it
 * moves. Returns the latest pulse for the status strip.
 */
export function useEngagementLiveRefresh(engagementId: string | undefined) {
  const qc = useQueryClient();
  const query = useEngagementActivityQuery(engagementId);
  const cursor = query.data?.cursor;
  // Cursor last acted on, per engagement. The first cursor seen is the
  // baseline — nothing to refresh, the page just loaded fresh data.
  const seen = React.useRef<{ engagementId: string; cursor: string } | null>(null);

  React.useEffect(() => {
    if (!engagementId || !cursor) return;
    const prev = seen.current;
    seen.current = { engagementId, cursor };
    if (!prev || prev.engagementId !== engagementId || prev.cursor === cursor) return;
    void qc.invalidateQueries({
      predicate: (q) => {
        const ns = q.queryKey[0];
        if (ns === ACTIVITY_KEY) return false;
        if (typeof ns === 'string' && ENTITY_SCOPED_NAMESPACES.has(ns)) return true;
        return q.queryKey.includes(engagementId);
      },
    });
  }, [engagementId, cursor, qc]);

  return query.data;
}
